import React, { useState, useEffect, useMemo, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getMasterEquipmentDefinitions,
  getMasterEquipment,
  createMasterEquipment,
  updateMasterEquipment,
  deleteMasterEquipment,
  bulkDeleteMasterEquipment,
  uploadMasterEquipmentImage,
  bulkUpdateMasterEquipment,
  bulkChangeEquipmentType,
  getTenantInfo,
  applyDefaultQuantityToAllUsers
} from '../services/api';
import EquipmentSelector from './EquipmentSelector';
import { combineImages, dataURLToBlob, blobToFile } from '../utils/imageCombiner';
import {
  Box,
  Flex,
  Button,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertDescription,
  IconButton,
  VStack,
  useDisclosure,
  HStack,
  Grid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Switch,
  useToast,
  Input,
  Image,
  Badge,
  GridItem,
  CloseButton,
  useBreakpointValue,
  Heading,
  Spinner,
  Checkbox,
  Select,
  Menu,
  MenuButton,
  MenuList,
  MenuItem
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, AddIcon, DeleteIcon, StarIcon, ArrowUpIcon, ArrowDownIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import { useSwipeable } from 'react-swipeable';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

function MasterSelectedEquipmentModal({ item, config, isTsubo, isOpen, onClose, isNew = false, onReloadItems }) {
    if (!isOpen) return null;
    const { isFunctionEnabled } = useAuth();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [editValues, setEditValues] = useState({});
    const navigate = useNavigate();
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
    const [images, setImages] = useState(isNew ? [] : item.images && item.images.length > 0 ? item.images : []);
    const [expandedImageIndex, setExpandedImageIndex] = useState(null);
    const { isOpen: isImageExpanded, onOpen: onImageExpand, onClose: onImageClose } = useDisclosure();
    const [useFilenameAsDescription, setUseFilenameAsDescription] = useState(false);
    
    // 画像合成機能のstate
    const [combineMethod, setCombineMethod] = useState('match_height');
    const [scaleRatio, setScaleRatio] = useState(1.0);
    const [combiningImages, setCombiningImages] = useState(false);
    const [combinedImageUrl, setCombinedImageUrl] = useState(null);
    const [savingCombinedImage, setSavingCombinedImage] = useState(false);
    const [selectedLeftImage, setSelectedLeftImage] = useState(0);
    const [selectedRightImage, setSelectedRightImage] = useState(1);

    // 拡大モーダル用のスワイプ機能の設定
    const modalSwipeHandlers = useSwipeable({
      onSwipedLeft: () => {
        if (imageUrls.length > 1) {
          const newIndex = expandedImageIndex < imageUrls.length - 1 ? expandedImageIndex + 1 : 0;
          setExpandedImageIndex(newIndex);
        }
      },
      onSwipedRight: () => {
        if (imageUrls.length > 1) {
          const newIndex = expandedImageIndex > 0 ? expandedImageIndex - 1 : imageUrls.length - 1;
          setExpandedImageIndex(newIndex);
        }
      },
      preventDefaultTouchmoveEvent: true,
      trackMouse: false
    });

    // 通常の画像セクション用のスワイプ機能の設定
    const imageSwipeHandlers = useSwipeable({
      onSwipedLeft: () => {
        if (imageUrls.length > 1) {
          const newIndex = currentIndex < imageUrls.length - 1 ? currentIndex + 1 : 0;
          setCurrentIndex(newIndex);
        }
      },
      onSwipedRight: () => {
        if (imageUrls.length > 1) {
          const newIndex = currentIndex > 0 ? currentIndex - 1 : imageUrls.length - 1;
          setCurrentIndex(newIndex);
        }
      },
      preventDefaultTouchmoveEvent: true,
      trackMouse: false
    });
  
    // 画像データの配列を取得（URL + 説明文）
    const imageData = React.useMemo(() => {
      // 編集中の画像配列がある場合はそれを使用
      if (images && images.length > 0) {
        return images.map(img => {
          if (typeof img === 'string') {
            return { url: img, description: '' };
          }
          return { url: img.url || img, description: img.description || '' };
        });
      }
      
      if (!item) return [];
      
      // 新形式（images配列）をチェック
      if (item.images && item.images.length > 0) {
        return item.images.map(img => {
          if (typeof img === 'string') {
            return { url: img, description: '' };
          }
          return { url: img.url || img, description: img.description || '' };
        });
      }
      
      // 旧形式（個別のURL）をチェック
      const urls = [];
      for (let i = 1; i <= 5; i++) {
        const url = item[`image${i}_sas_url`];
        if (url) urls.push({ url, description: '' });
      }
      return urls;
    }, [item, images]);

    // 下位互換性のため、URLのみの配列も作成
    const imageUrls = React.useMemo(() => {
      return imageData.map(img => img.url);
    }, [imageData]);
  
    useEffect(() => {
      setImages(isNew ? [] : item.images && item.images.length > 0 ? item.images : []);
    }, [item, isNew]);
  
    useEffect(() => {
      setCurrentIndex(0);
      if (item) {
        setEditValues({ ...item });
      }
      // 画像合成関連のstateをリセット
      setCombineMethod('match_height');
      setScaleRatio(1.0);
      setCombinedImageUrl(null);
      setSelectedLeftImage(0);
      setSelectedRightImage(1);
    }, [item, isNew]);
  
    const goToNext = useCallback(() => {
      if (imageUrls.length > 1) {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % imageUrls.length);
      }
    }, [imageUrls.length]);
  
    const goToPrevious = useCallback(() => {
      if (imageUrls.length > 1) {
        setCurrentIndex((prevIndex) => (prevIndex - 1 + imageUrls.length) % imageUrls.length);
      }
    }, [imageUrls.length]);
  
    useEffect(() => {
      if (imageUrls.length > 1) {
        const timer = setTimeout(goToNext, 3000);
        return () => clearTimeout(timer);
      }
    }, [currentIndex, imageUrls.length, goToNext]);


  
    if (!item) return null;
  
    const handlePrevImageClick = (e) => { e.stopPropagation(); goToPrevious(); };
    const handleNextImageClick = (e) => { e.stopPropagation(); goToNext(); };
    
    const handleImageExpand = (index) => {
      setExpandedImageIndex(index);
      onImageExpand();
    };
  
    // Chakra UIのカラーモードに基づく色の設定
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const subTextColor = useColorModeValue('gray.600', 'gray.400');
  
    const handleSave = async () => {
      try {
        // デフォルト数量の変更チェック
        const newDefaultQuantity = editValues.default_quantity;
        const oldDefaultQuantity = isNew ? undefined : item.default_quantity;
        const defaultQuantityChanged = newDefaultQuantity !== oldDefaultQuantity;
        const shouldApplyToAllUsers = newDefaultQuantity && newDefaultQuantity > 0 && defaultQuantityChanged;

        if (shouldApplyToAllUsers) {
          const confirmed = window.confirm(
            `デフォルト数量が ${oldDefaultQuantity || 0} から ${newDefaultQuantity} に変更されました。\n` +
            `これにより、テナント内の全ユーザでこの設備が選択中になり、数量が ${newDefaultQuantity} に設定されます。\n` +
            `続行しますか？`
          );
          if (!confirmed) {
            return;
          }
        }

        // 編集可能なフィールドのみ抽出
        const editableFieldNames = config.fields
          .filter(field =>
            ![
              'id', 'user_id', 'created_at', 'updated_at', 'is_liked', 'is_fixed', 'locked_at',
              'signature_url', 'memo', 'material_files', 'preferenceId'
            ].includes(field.name)
          )
          .map(field => field.name);
  
        const payload = {};
        editableFieldNames.forEach(name => {
          if (editValues.hasOwnProperty(name)) {
            payload[name] = editValues[name];
          }
        });
        payload['external_link_url'] = editValues.external_link_url;
        payload['external_link_name'] = editValues.external_link_name;
        
        let savedItemId;
        if (isNew) {
          const result = await createMasterEquipment(
            config.key,
            payload
          );
          savedItemId = result.id;
        } else {
          await updateMasterEquipment(
            config.key,
            item.id,
            payload
          );
          savedItemId = item.id;
        }

        // デフォルト数量が変更された場合のみ、全ユーザ設定を更新
        if (shouldApplyToAllUsers) {
          try {
            await applyDefaultQuantityToAllUsers(config.key, savedItemId, newDefaultQuantity);
            alert(`マスタ設備を保存し、全ユーザに数量 ${newDefaultQuantity} で適用しました。`);
          } catch (bulkError) {
            console.error('全ユーザ設定の更新に失敗:', bulkError);
            alert('マスタ設備は保存されましたが、全ユーザ設定の更新に失敗しました。');
          }
        }

        // アイテムリストを再読み込み
        if (onReloadItems) {
          onReloadItems();
        }

        if (onClose) {
          onClose(); // 編集完了後にモーダルを閉じる
        }
      } catch (e) {
        alert('保存に失敗しました');
      }
    };
  
    const handleEditFloorplan = () => {
      const url = `/admin/master-equipment/master-floor`;
      navigate(url, { state: { 
        floorRooms: item.rooms || [], 
        mode: isNew ? 'new_master' : 'edit_master', 
        item_id: isNew ? null : item.id,
        images: item.images || [],
        masterEquipmentData: item
      }});
    };

    const handleUploadImage = async (file, description = '') => {
      setUploading(true);
      const res = await uploadMasterEquipmentImage(file, description);
      const newImageObj = { url: res.url, description: res.description || description };
      setImages([...images, newImageObj]);
      setEditValues(v => ({ ...v, images: [...images, newImageObj] }));
      setUploading(false);
    };

    // ファイル名から拡張子を除去するヘルパー関数
    const getFilenameWithoutExtension = (filename) => {
      const lastDotIndex = filename.lastIndexOf('.');
      if (lastDotIndex === -1) return filename;
      return filename.substring(0, lastDotIndex);
    };

    const handleUploadMultipleImages = async (files, descriptions = []) => {
      if (!files || files.length === 0) return;
      
      setUploading(true);
      setUploadProgress({ current: 0, total: files.length });
      const newImageObjs = [];
      
      try {
        // 複数ファイルを順次アップロード
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          let description = descriptions[i] || '';
          
          // チェックボックスがONの場合、ファイル名から拡張子を除いたものを説明文として使用
          if (useFilenameAsDescription && !description) {
            description = getFilenameWithoutExtension(file.name);
          }
          
          setUploadProgress({ current: i + 1, total: files.length });
          const res = await uploadMasterEquipmentImage(file, description);
          newImageObjs.push({ url: res.url, description: res.description || description });
        }
        
        // すべてのアップロードが完了してから一括で更新
        const updatedImages = [...images, ...newImageObjs];
        setImages(updatedImages);
        setEditValues(v => ({ ...v, images: updatedImages }));
      } catch (error) {
        console.error('画像アップロードエラー:', error);
        alert('画像のアップロードに失敗しました');
      } finally {
        setUploading(false);
        setUploadProgress({ current: 0, total: 0 });
      }
    };

    // 画像合成ハンドラー
    const handleCombineImages = async () => {
      if (images.length < 2) {
        alert('画像が2つ以上必要です');
        return;
      }

      if (selectedLeftImage === selectedRightImage) {
        alert('左側と右側で異なる画像を選択してください');
        return;
      }

      setCombiningImages(true);
      try {
        const leftImageUrl = typeof images[selectedLeftImage] === 'string' ? images[selectedLeftImage] : images[selectedLeftImage].url;
        const rightImageUrl = typeof images[selectedRightImage] === 'string' ? images[selectedRightImage] : images[selectedRightImage].url;

        const options = {
          matchHeight: combineMethod === 'match_height',
          scaleRatio: combineMethod === 'scale_ratio' ? scaleRatio : 1.0,
          backgroundColor: '#FFFFFF'
        };

        const combinedDataUrl = await combineImages(leftImageUrl, rightImageUrl, options);
        setCombinedImageUrl(combinedDataUrl);
      } catch (error) {
        console.error('画像合成エラー:', error);
        alert('画像の合成に失敗しました: ' + error.message);
      } finally {
        setCombiningImages(false);
      }
    };

    // 合成画像を保存するハンドラー
    const handleSaveCombinedImage = async () => {
      if (!combinedImageUrl) return;

      setSavingCombinedImage(true);
      try {
        // DataURLをBlobに変換
        const blob = dataURLToBlob(combinedImageUrl);
        
        // BlobをFileオブジェクトに変換
        const fileName = `combined_${Date.now()}.png`;
        const file = blobToFile(blob, fileName);

        // 合成画像をアップロード
        const description = `合成画像 (${combineMethod === 'match_height' ? '高さ合わせ' : `${scaleRatio}倍拡大`})`;
        const res = await uploadMasterEquipmentImage(file, description);
        
        const newImageObj = { url: res.url, description: res.description || description };
        
        // 合成画像を画像リストに追加
        const updatedImages = [...images, newImageObj];
        setImages(updatedImages);
        setEditValues(v => ({ ...v, images: updatedImages }));
        
        // 合成結果をクリア
        setCombinedImageUrl(null);
        
        alert('合成画像を保存しました');
      } catch (error) {
        console.error('合成画像保存エラー:', error);
        alert('合成画像の保存に失敗しました: ' + error.message);
      } finally {
        setSavingCombinedImage(false);
      }
    };
  
    return (
      <>
      <Modal isOpen={true} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent maxW="800px" maxH="90vh" overflowY="auto">
          <ModalHeader borderBottomWidth="1px">
            {isNew ? '新規マスタ設備' : `${item.manufacturer} - ${item.name}`}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={4}>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
              {/* 画像セクション */}
              <Box position="relative" borderWidth="1px" borderColor={borderColor} borderRadius="md" overflow="hidden" minH="300px">
                {imageUrls.length > 0 ? (
                  <>
                    <Box {...imageSwipeHandlers}>
                      <Image
                        src={imageUrls[currentIndex]}
                        alt={`${item.name} - 画像 ${currentIndex + 1}`}
                        w="100%"
                        h="280px"
                        objectFit="contain"
                        cursor="pointer"
                        onClick={() => handleImageExpand(currentIndex)}
                        title="クリックして拡大表示"
                      />
                      {imageData[currentIndex]?.description && (
                        <Text
                          fontSize="sm"
                          color={subTextColor}
                          textAlign="center"
                          mt={2}
                          px={2}
                          bg="gray.50"
                          borderRadius="md"
                          py={1}
                        >
                          {imageData[currentIndex].description}
                        </Text>
                      )}
                    </Box>
                    {imageUrls.length > 1 && (
                      <>
                        <IconButton
                          icon={<ChevronLeftIcon />}
                          aria-label="前の画像"
                          position="absolute"
                          left={2}
                          top="50%"
                          transform="translateY(-50%)"
                          onClick={handlePrevImageClick}
                          colorScheme="blackAlpha"
                        />
                        <IconButton
                          icon={<ChevronRightIcon />}
                          aria-label="次の画像"
                          position="absolute"
                          right={2}
                          top="50%"
                          transform="translateY(-50%)"
                          onClick={handleNextImageClick}
                          colorScheme="blackAlpha"
                        />
                      </>
                    )}
                  </>
                ) : (
                  <Flex
                    w="100%"
                    h="300px"
                    bg="gray.100"
                    align="center"
                    justify="center"
                    color="gray.500"
                  >
                    画像なし
                  </Flex>
                )}
              </Box>
  
              {/* 詳細情報セクション */}
              <VStack align="stretch" spacing={4}>
                  <>
                    {config.fields?.map(field => {
                      if ([
                        'id', 'user_id', 'created_at', 'updated_at', 'images', 'signature_url', 'locked_at', 'preferenceId', 'is_liked', 'is_fixed', 'memo', 'material_files'
                      ].includes(field.name)) return null;
                      // boolean型
                      if (field.type === 'boolean') {
                        return (
                          <Box key={field.name}>
                            <Text fontSize="sm" color={subTextColor}>{field.label}</Text>
                            <input
                              type="checkbox"
                              checked={!!editValues[field.name]}
                              onChange={e => setEditValues(v => ({ ...v, [field.name]: e.target.checked }))}
                            />
                          </Box>
                        );
                      }
                      // select型
                      if (field.type === 'select') {
                        return (
                          <Box key={field.name}>
                            <Text fontSize="sm" color={subTextColor}>{field.label}</Text>
                            <select
                              value={editValues[field.name] || ''}
                              onChange={e => setEditValues(v => ({ ...v, [field.name]: e.target.value }))}
                            >
                              <option value="">選択してください</option>
                              {field.options?.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          </Box>
                        );
                      }
                      // number/string型
                      return (
                        <Box key={field.name}>
                          <Text fontSize="sm" color={subTextColor} mb={1}>{field.label}</Text>
                          {field.name === 'default_quantity' && (
                            <Text fontSize="xs" color="orange.500" mb={2}>
                              ※ デフォルト数量を設定すると、テナント内の全ユーザにてこの設備が選択中になり、指定した数量がデフォルトとして設定されます
                            </Text>
                          )}
                          <Input
                            type={field.type === 'number' ? 'number' : 'text'}
                            value={editValues[field.name] ?? ''}
                            onChange={e => setEditValues(v => ({ ...v, [field.name]: field.type === 'number' ? Number(e.target.value) : e.target.value }))}
                            size="sm" // サイズを調整
                          />
  
                        </Box>
                      );
                  
                    })}
                    
                    {/* 外部リンクフィールド */}
                    <Box>
                      <Text fontSize="sm" color={subTextColor} mb={1}>外部リンクURL</Text>
                      <Input
                        type="url"
                        placeholder="https://example.com"
                        value={editValues.external_link_url || ''}
                        onChange={e => setEditValues(v => ({ ...v, external_link_url: e.target.value }))}
                        size="sm"
                      />
                    </Box>
                    <Box>
                      <Text fontSize="sm" color={subTextColor} mb={1}>外部リンク表示名</Text>
                      <Input
                        type="text"
                        placeholder="詳細を見る"
                        value={editValues.external_link_name || ''}
                        onChange={e => setEditValues(v => ({ ...v, external_link_name: e.target.value }))}
                        size="sm"
                      />
                    </Box>
                    
                    <Box mt={2}>
                      <Text fontSize="xs" color="gray.600" mb={1}>
                        画像をアップロード（複数選択可能）
                      </Text>
                      <Checkbox
                        size="sm"
                        isChecked={useFilenameAsDescription}
                        onChange={(e) => setUseFilenameAsDescription(e.target.checked)}
                        mb={2}
                      >
                        <Text fontSize="xs" color="gray.600">
                          ファイル名を画像の注釈として使用する
                        </Text>
                      </Checkbox>
                      <Input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={async (e) => {
                          const files = Array.from(e.target.files);
                          if (files.length === 0) return;
                          await handleUploadMultipleImages(files);
                          // ファイル選択をリセット（同じファイルを再選択できるように）
                          e.target.value = '';
                        }}
                        disabled={uploading}
                      />
                      {uploading && (
                        <Text fontSize="xs" color="gray.500">
                          {uploadProgress.total > 1 
                            ? `アップロード中... (${uploadProgress.current}/${uploadProgress.total})`
                            : 'アップロード中...'
                          }
                        </Text>
                      )}
                      {images && images.length > 0 && (
                        <Box mt={2}>
                          <Text fontSize="xs" color="gray.600" mb={2}>
                            画像の並び替えと説明文の編集
                          </Text>
                          <Text fontSize="xs" color="gray.500" mb={2}>
                            ↑↓ボタンで順番を変更できます。最初の画像がメイン画像として表示されます。
                            {images.length === 1 && "（画像が2つ以上必要です）"}
                          </Text>
                          {images.map((imageObj, index) => {
                            const img = typeof imageObj === 'string' ? { url: imageObj, description: '' } : imageObj;
                            return (
                              <Box key={img.url || index} mb={3} p={2} border="1px solid" borderColor="gray.200" borderRadius="md">
                                <HStack spacing={3} align="start">
                                  <Box position="relative">
                                    <img 
                                      src={img.url} 
                                      alt={`設備画像 ${index + 1}`} 
                                      style={{ 
                                        width: 80, 
                                        height: 80,
                                        objectFit: 'contain',
                                        cursor: 'pointer',
                                        border: index === 0 ? '2px solid #3182CE' : '1px solid #E2E8F0',
                                        borderRadius: '4px'
                                      }}
                                      onClick={() => handleImageExpand(index)}
                                      title="クリックして拡大表示"
                                    />
                                    {/* 順番番号バッジ */}
                                    <Badge
                                      position="absolute"
                                      top="-8px"
                                      right="-8px"
                                      size="sm"
                                      colorScheme={index === 0 ? "yellow" : "gray"}
                                      borderRadius="full"
                                      minW="20px"
                                      textAlign="center"
                                    >
                                      {index + 1}
                                    </Badge>
                                    {index === 0 && (
                                      <Badge mt={1} size="sm" colorScheme="yellow">メイン</Badge>
                                    )}
                                  </Box>
                                  <VStack flex={1} align="stretch" spacing={2}>
                                    <Text fontSize="xs" color="gray.600">説明文:</Text>
                                    <Input
                                      size="sm"
                                      placeholder="画像の説明を入力..."
                                      value={img.description || ''}
                                      onChange={(e) => {
                                        const newImages = [...images];
                                        if (typeof newImages[index] === 'string') {
                                          newImages[index] = { url: newImages[index], description: e.target.value };
                                        } else {
                                          newImages[index] = { ...newImages[index], description: e.target.value };
                                        }
                                        setImages(newImages);
                                        setEditValues(v => ({ ...v, images: newImages }));
                                      }}
                                    />
                                    <HStack spacing={2}>
                                      {/* 上に移動ボタン */}
                                      {index > 0 && (
                                        <IconButton
                                          size="xs"
                                          colorScheme="yellow"
                                          icon={<ArrowUpIcon />}
                                          onClick={() => {
                                            const newImages = [...images];
                                            const temp = newImages[index];
                                            newImages[index] = newImages[index - 1];
                                            newImages[index - 1] = temp;
                                            
                                            setImages(newImages);
                                            setEditValues(v => ({ ...v, images: newImages }));
                                            // メイン画像が変更された場合はインデックスを更新
                                            if (index === 1) {
                                              setCurrentIndex(0);
                                            } else if (currentIndex === index) {
                                              setCurrentIndex(index - 1);
                                            } else if (currentIndex === index - 1) {
                                              setCurrentIndex(index);
                                            }
                                          }}
                                          aria-label="上に移動"
                                        />
                                      )}
                                      {/* 下に移動ボタン */}
                                      {index < images.length - 1 && (
                                        <IconButton
                                          size="xs"
                                          colorScheme="yellow"
                                          icon={<ArrowDownIcon />}
                                          onClick={() => {
                                            const newImages = [...images];
                                            const temp = newImages[index];
                                            newImages[index] = newImages[index + 1];
                                            newImages[index + 1] = temp;
                                            
                                            setImages(newImages);
                                            setEditValues(v => ({ ...v, images: newImages }));
                                            // メイン画像が変更された場合はインデックスを更新
                                            if (index === 0) {
                                              setCurrentIndex(1);
                                            } else if (currentIndex === index) {
                                              setCurrentIndex(index + 1);
                                            } else if (currentIndex === index + 1) {
                                              setCurrentIndex(index);
                                            }
                                          }}
                                          aria-label="下に移動"
                                        />
                                      )}
                                      {/* メインに設定ボタン */}
                                      {index !== 0 && (
                                        <Button
                                          size="xs"
                                          colorScheme="yellow"
                                          onClick={() => {
                                            // 選択した画像を配列の先頭に移動
                                            const newImages = [...images];
                                            const selectedImage = newImages[index];
                                            newImages.splice(index, 1); // 現在の位置から削除
                                            newImages.unshift(selectedImage); // 先頭に追加
                                            
                                            setImages(newImages);
                                            setEditValues(v => ({ ...v, images: newImages }));
                                            setCurrentIndex(0); // メイン画像を表示
                                          }}
                                        >
                                          メインに設定
                                        </Button>
                                      )}
                                      {/* 削除ボタン */}
                                      <Button
                                        size="xs"
                                        colorScheme="red"
                                        onClick={() => {
                                          const newImages = images.filter((_, i) => i !== index);
                                          setImages(newImages);
                                          setEditValues(v => ({ ...v, images: newImages }));
                                        }}
                                      >
                                        削除
                                      </Button>
                                    </HStack>
                                  </VStack>
                                </HStack>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                    
                    {/* 画像合成機能 */}
                    {images && images.length >= 2 && (
                      <Box mt={4} p={3} border="1px solid" borderColor="yellow.400" borderRadius="md" bg="yellow.50">
                        <Text fontSize="sm" fontWeight="bold" color="yellow.800" mb={2}>
                          画像合成機能
                        </Text>
                        <Text fontSize="xs" color="yellow.700" mb={3}>
                          2つの画像を横並びで合成できます。合成したい画像を選択してください。
                        </Text>
                        
                        <VStack spacing={3} align="stretch">
                          {/* 画像選択 */}
                          <Box>
                            <Text fontSize="xs" color="yellow.700" mb={2}>合成する画像を選択:</Text>
                            <HStack spacing={3}>
                              <VStack spacing={1} align="stretch">
                                <Text fontSize="xs" color="yellow.700">左側の画像:</Text>
                                <select
                                  value={selectedLeftImage}
                                  onChange={(e) => setSelectedLeftImage(parseInt(e.target.value))}
                                  style={{
                                    width: '100%',
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    border: '1px solid #E2E8F0',
                                    borderRadius: '4px'
                                  }}
                                >
                                  {images.map((img, index) => {
                                    const imgUrl = typeof img === 'string' ? img : img.url;
                                    const imgDesc = typeof img === 'string' ? '' : (img.description || '');
                                    return (
                                      <option key={index} value={index}>
                                        {index + 1}: {imgDesc || `画像${index + 1}`}
                                      </option>
                                    );
                                  })}
                                </select>
                              </VStack>
                              <VStack spacing={1} align="stretch">
                                <Text fontSize="xs" color="yellow.700">右側の画像:</Text>
                                <select
                                  value={selectedRightImage}
                                  onChange={(e) => setSelectedRightImage(parseInt(e.target.value))}
                                  style={{
                                    width: '100%',
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    border: '1px solid #E2E8F0',
                                    borderRadius: '4px'
                                  }}
                                >
                                  {images.map((img, index) => {
                                    const imgUrl = typeof img === 'string' ? img : img.url;
                                    const imgDesc = typeof img === 'string' ? '' : (img.description || '');
                                    return (
                                      <option key={index} value={index}>
                                        {index + 1}: {imgDesc || `画像${index + 1}`}
                                      </option>
                                    );
                                  })}
                                </select>
                              </VStack>
                            </HStack>
                          </Box>
                          
                          <Box>
                            <Text fontSize="xs" color="yellow.700" mb={1}>合成方法:</Text>
                            <select
                              value={combineMethod}
                              onChange={(e) => setCombineMethod(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '4px 8px',
                                fontSize: '12px',
                                border: '1px solid #E2E8F0',
                                borderRadius: '4px'
                              }}
                            >
                              <option value="match_height">高さを合わせる</option>
                              <option value="scale_ratio">拡大率を指定</option>
                            </select>
                          </Box>
                          
                          {combineMethod === 'scale_ratio' && (
                            <Box>
                              <Text fontSize="xs" color="yellow.700" mb={1}>右側画像の拡大率:</Text>
                              <Input
                                type="number"
                                step="0.1"
                                min="0.1"
                                max="5.0"
                                value={scaleRatio}
                                onChange={(e) => setScaleRatio(parseFloat(e.target.value) || 1.0)}
                                size="sm"
                                placeholder="1.0"
                              />
                            </Box>
                          )}
                          
                                                     <Button
                             size="sm"
                             colorScheme="yellow"
                             onClick={handleCombineImages}
                             isLoading={combiningImages}
                             disabled={images.length < 2 || selectedLeftImage === selectedRightImage}
                           >
                             画像を合成
                           </Button>
                           {selectedLeftImage === selectedRightImage && (
                             <Text fontSize="xs" color="red.500">
                               左側と右側で異なる画像を選択してください
                             </Text>
                           )}
                          
                          {combinedImageUrl && (
                            <Box mt={2}>
                              <Text fontSize="xs" color="yellow.700" mb={1}>合成結果:</Text>
                              <Box position="relative" border="1px solid" borderColor="yellow.300" borderRadius="md" overflow="hidden">
                                <img
                                  src={combinedImageUrl}
                                  alt="合成された画像"
                                  style={{
                                    width: '100%',
                                    maxHeight: '200px',
                                    objectFit: 'contain'
                                  }}
                                />
                              </Box>
                              <HStack spacing={2} mt={2}>
                                <Button
                                  size="xs"
                                  colorScheme="yellow"
                                  onClick={handleSaveCombinedImage}
                                  isLoading={savingCombinedImage}
                                >
                                  合成画像を保存
                                </Button>
                                <Button
                                  size="xs"
                                  colorScheme="gray"
                                  onClick={() => setCombinedImageUrl(null)}
                                >
                                  キャンセル
                                </Button>
                              </HStack>
                            </Box>
                          )}
                        </VStack>
                      </Box>
                    )}
                    
                    <Box>
                        {config.key === 'floorplan' && isFunctionEnabled('floor_plan') && (
                            <Button bg="black" color="white" onClick={handleEditFloorplan}>間取りを編集</Button>
                        )}
                    </Box>
                    <HStack>
                      <Button bg="black" color="white" onClick={handleSave}>保存</Button>
                      <Button onClick={() => onClose()}>キャンセル</Button>
                    </HStack>
                  </>
                </VStack>
            </Grid>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* 画像拡大表示モーダル */}
      <Modal isOpen={isImageExpanded} onClose={onImageClose} size="6xl" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="transparent" boxShadow="none" maxW={{ base: "100vw", md: "95vw" }} maxH={{ base: "100vh", md: "95vh" }}>
          <ModalCloseButton 
            color="white" 
            bg="blackAlpha.600" 
            _hover={{ bg: "blackAlpha.800" }}
            size={{ base: "md", md: "lg" }}
            zIndex={20}
            position="fixed"
            top={{ base: 2, md: 4 }}
            right={{ base: 2, md: 4 }}
          />
          <ModalBody p={0} display="flex" alignItems="center" justifyContent="center">
            {expandedImageIndex !== null && imageUrls[expandedImageIndex] && (
              <Box position="relative" w="100%" h="100%">
                <TransformWrapper
                  initialScale={1}
                  minScale={0.5}
                  maxScale={4}
                  centerOnInit={true}
                  limitToBounds={false}
                  smooth={true}
                  wheel={{
                    step: 0.1,
                    wheelEnabled: true,
                    touchPadEnabled: true,
                  }}
                  pinch={{
                    step: 1,
                    disabled: false,
                  }}
                  doubleClick={{
                    step: 2,
                    mode: "toggle",
                  }}
                                  onTransformed={(state) => {
                  // ズーム状態を追跡
                }}
                >
                  {({ zoomIn, zoomOut, resetTransform, state }) => (
                    <TransformComponent
                      wrapperStyle={{
                        width: "100%",
                        height: "100%",
                        maxWidth: "100%",
                        maxHeight: "100%",
                        touchAction: "none",
                      }}
                      contentStyle={{
                        width: "100%",
                        height: "100%",
                        touchAction: "none",
                      }}
                    >
                      <Box
                        w={{ base: "95vw", md: "800px" }}
                        h={{ base: "70vh", md: "600px" }}
                        bg="black"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        position="relative"
                      >
                        <Image
                          src={imageUrls[expandedImageIndex]}
                          alt={`${item.name} - 拡大画像 ${expandedImageIndex + 1}`}
                          maxW={{ base: "95vw", md: "800px" }}
                          maxH={{ base: "70vh", md: "600px" }}
                          objectFit="contain"
                          draggable={false}
                          userSelect="none"
                          style={{
                            touchAction: "none",
                            WebkitTouchCallout: "none",
                            WebkitUserSelect: "none",
                            KhtmlUserSelect: "none",
                            MozUserSelect: "none",
                            msUserSelect: "none",
                            userSelect: "none",
                          }}
                        />
                        {imageData[expandedImageIndex]?.description && (
                          <Text
                            fontSize="lg"
                            color="white"
                            textAlign="center"
                            bg="blackAlpha.600"
                            px={4}
                            py={2}
                            borderRadius="md"
                            maxW="80%"
                            position="absolute"
                            bottom={{ base: 20, md: 12 }}
                            left="50%"
                            transform="translateX(-50%)"
                            zIndex={5}
                          >
                            {imageData[expandedImageIndex].description}
                          </Text>
                        )}
                        {/* ズームインジケーター */}
                        {state && state.scale !== 1 && (
                          <Box
                            position="absolute"
                            top={4}
                            left="50%"
                            transform="translateX(-50%)"
                            bg="blackAlpha.600"
                            color="white"
                            px={3}
                            py={1}
                            borderRadius="md"
                            fontSize="sm"
                            zIndex={10}
                          >
                            ズーム: {Math.round(state.scale * 100)}%
                          </Box>
                        )}
                        {/* ズームコントロールボタン */}
                        <HStack
                          position="absolute"
                          bottom={4}
                          right={4}
                          spacing={2}
                          zIndex={10}
                        >
                          <IconButton
                            icon={<Text fontSize="sm">+</Text>}
                            onClick={() => zoomIn()}
                            bg="blackAlpha.600"
                            color="white"
                            _hover={{ bg: "blackAlpha.800" }}
                            size="sm"
                            aria-label="ズームイン"
                          />
                          <IconButton
                            icon={<Text fontSize="sm">-</Text>}
                            onClick={() => zoomOut()}
                            bg="blackAlpha.600"
                            color="white"
                            _hover={{ bg: "blackAlpha.800" }}
                            size="sm"
                            aria-label="ズームアウト"
                          />
                          <IconButton
                            icon={<Text fontSize="sm">⟲</Text>}
                            onClick={() => resetTransform()}
                            bg="blackAlpha.600"
                            color="white"
                            _hover={{ bg: "blackAlpha.800" }}
                            size="sm"
                            aria-label="リセット"
                          />
                        </HStack>
                      </Box>
                    </TransformComponent>
                  )}
                </TransformWrapper>
                
                {/* 拡大表示中の画像切り替えボタン */}
                {imageUrls.length > 1 && (
                  <>
                    <IconButton
                      icon={<ChevronLeftIcon />}
                      aria-label="前の画像"
                      position="absolute"
                      left={{ base: 2, md: 4 }}
                      top="50%"
                      transform="translateY(-50%)"
                      onClick={() => {
                        const newIndex = expandedImageIndex > 0 ? expandedImageIndex - 1 : imageUrls.length - 1;
                        setExpandedImageIndex(newIndex);
                      }}
                      colorScheme="blackAlpha"
                      size={{ base: "md", md: "lg" }}
                    />
                    <IconButton
                      icon={<ChevronRightIcon />}
                      aria-label="次の画像"
                      position="absolute"
                      right={{ base: 2, md: 4 }}
                      top="50%"
                      transform="translateY(-50%)"
                      onClick={() => {
                        const newIndex = expandedImageIndex < imageUrls.length - 1 ? expandedImageIndex + 1 : 0;
                        setExpandedImageIndex(newIndex);
                      }}
                      colorScheme="blackAlpha"
                      size={{ base: "md", md: "lg" }}
                    />
                  </>
                )}
                
                <Badge
                  position="absolute"
                  bottom={{ base: 8, md: 4 }}
                  left="50%"
                  transform="translateX(-50%)"
                  bg="blackAlpha.600"
                  color="white"
                  fontSize={{ base: "xs", md: "sm" }}
                  px={{ base: 2, md: 3 }}
                  py={{ base: 0.5, md: 1 }}
                >
                  {expandedImageIndex + 1} / {imageUrls.length}
                </Badge>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      </>
    );
  }

const MasterEquipmentList = forwardRef(({
  items,
  config,
  equipmentDefinitions,
  isTsubo = false,
  onReloadItems = () => {},
  orderChanged,
  setOrderChanged,
  savingOrder,
  setSavingOrder
}, ref) => {
  const { isOpen: isMasterModalOpen, onOpen: onMasterModalOpen, onClose: onMasterModalClose } = useDisclosure();
  const toast = useToast();

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const subTextColor = useColorModeValue('gray.600', 'gray.400');
  const tileBg = useColorModeValue('white', 'gray.800');
  const tileBorder = useColorModeValue('gray.200', 'gray.600');
  const [selectedItems, setSelectedItems] = useState([]);

  const [selectedItemId, setSelectedItemId] = useState(null);

  const [isNew, setIsNew] = useState(false);
  const [uploadingMulti, setUploadingMulti] = useState(false);
  const [bulkDescription, setBulkDescription] = useState('');
  const [useBulkFilenameAsDescription, setUseBulkFilenameAsDescription] = useState(false);

  const [localItems, setLocalItems] = useState([]);

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '価格未定';
    return (
      <>
        <Text as="span" fontSize="lg" fontWeight="bold">¥{price.toLocaleString()}</Text>
        <Text as="span" fontSize="xs" color="gray.600"> (税抜)</Text>
      </>
    );
  };

  const handleMultiSelectChange = (itemId, e) => {
    if (e.target.checked) {
      setSelectedItems(prev => [...prev, itemId]);
    } else {
      setSelectedItems(prev => prev.filter(id => id !== itemId));
    } 
  };

  // ファイル名から拡張子を除去するヘルパー関数（一括アップロード用）
  const getBulkFilenameWithoutExtension = (filename) => {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) return filename;
    return filename.substring(0, lastDotIndex);
  };

  const handleUploadImageMulti = async (file, selectedItems, description) => {
    setUploadingMulti(true);
    try {
      // チェックボックスがONで説明文が空の場合、ファイル名から拡張子を除いたものを使用
      let finalDescription = description;
      if (useBulkFilenameAsDescription && !description) {
        finalDescription = getBulkFilenameWithoutExtension(file.name);
      }
      
      const res = await uploadMasterEquipmentImage(file, finalDescription);
      const newImageObj = { url: res.url, description: res.description || finalDescription };
      
      // 選択されたアイテムそれぞれに画像を追加（順次実行）
      for (const itemId of selectedItems) {
        const item = items.find(item => item.id === itemId);
        if (item) {
          let updatedImages;
          if (item.images && Array.isArray(item.images)) {
            // 既存の画像データ形式を統一
            const normalizedImages = item.images.map(img => {
              if (typeof img === 'string') {
                return { url: img, description: '' };
              }
              return { url: img.url || img, description: img.description || '' };
            });
            updatedImages = [...normalizedImages, newImageObj];
          } else {
            updatedImages = [newImageObj];
          }
          
          // リクエストを順次実行（認証トークンの競合を避けるため）
          await updateMasterEquipment(config.key, item.id, { images: updatedImages });
          
          // 各リクエスト間に短い遅延を追加
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      toast({
        title: "画像をアップロードしました。続けてアップロードする場合は、チェックボックス見直しの上、ファイルを再選択してください。",
        status: "success",
        duration: 10000,
        isClosable: true,
      });
      
      onReloadItems(); // リストを再読み込み
    } catch (error) {
      console.error('一括画像アップロードエラー:', error);
      toast({
        title: "エラー",
        description: error.message || "画像のアップロードに失敗しました",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploadingMulti(false);
      setBulkDescription(''); // 説明文をリセット
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    
    const confirmMessage = `選択した${selectedItems.length}件の設備を削除してもよいですか？この操作は取り消せません。`;
    if (!window.confirm(confirmMessage)) return;
    
    try {
      const result = await bulkDeleteMasterEquipment(config.key, selectedItems);
      console.log('Bulk delete result:', result);
      
      const deletedCount = result.deleted_count || 0;
      const failedCount = result.failed_deletions ? result.failed_deletions.length : 0;
      
      if (failedCount > 0) {
        toast({
          title: `${deletedCount}件削除しました（${failedCount}件失敗）`,
          description: `削除に失敗した設備があります`,
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: `${deletedCount}件の設備を削除しました`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
      
      setSelectedItems([]); // 選択をクリア
      onReloadItems();
    } catch (error) {
      console.error('一括削除エラー:', error);
      toast({
        title: "エラー",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleBulkChangeType = async (newEquipmentType) => {
    if (selectedItems.length === 0) return;
    
    const confirmMessage = `選択した${selectedItems.length}件の設備を「${newEquipmentType}」に変更してもよいですか？\n\nこの操作により：\n• 設備のタイプが変更されます\n• ユーザーの選択状態も新しいタイプに移行されます\n• デフォルト数量が設定されている場合は全ユーザーに適用されます\n\nこの操作は取り消せません。`;
    if (!window.confirm(confirmMessage)) return;
    
    try {
      const result = await bulkChangeEquipmentType(config.key, selectedItems, newEquipmentType);
      console.log('Bulk change type result:', result);
      
      const successCount = result.success_count || 0;
      const errorCount = result.error_count || 0;
      const userPreferencesUpdated = result.user_preferences_updated || 0;
      const defaultQuantityApplied = result.default_quantity_applied || 0;
      
      let description = '';
      if (userPreferencesUpdated > 0) {
        description += `ユーザー選択状態: ${userPreferencesUpdated}件移行\n`;
      }
      if (defaultQuantityApplied > 0) {
        description += `デフォルト数量適用: ${defaultQuantityApplied}件`;
      }
      
      if (errorCount > 0) {
        toast({
          title: `${successCount}件変更しました（${errorCount}件失敗）`,
          description: description || `変更に失敗した設備があります`,
          status: "warning",
          duration: 8000,
          isClosable: true,
        });
      } else {
        toast({
          title: `${successCount}件の設備を変更しました`,
          description: description,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }
      
      setSelectedItems([]); // 選択をクリア
      onReloadItems();
    } catch (error) {
      console.error('一括変更エラー:', error);
      toast({
        title: "エラー",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // 画像URLの取得
  const getImageUrl = (item) => {
    // 新しい形式（images配列）をチェック
    if (item.images && item.images.length > 0) {
      const firstImage = item.images[0];
      if (typeof firstImage === 'string') {
        return firstImage;
      }
      return firstImage.url || firstImage;
    }
    // 古い形式（個別のURL）をチェック
    if (item.image1_sas_url) {
      return item.image1_sas_url;
    }
    return null;
  };

  // 画像の説明文を取得
  const getImageDescription = (item) => {
    if (item.images && item.images.length > 0) {
      const firstImage = item.images[0];
      if (typeof firstImage === 'object' && firstImage.description) {
        return firstImage.description;
      }
    }
    return '';
  };

  const selectedItem = useMemo(() => {
    if (selectedItemId === null){
      const item = {
        item_type: config.key,
      };
      return item;
    }
    return items.find(item => item.id === selectedItemId);
  }, [selectedItemId, items]);

  const onItemSelect = (itemId) => {
    setIsNew(false);
    setSelectedItemId(itemId);
    onMasterModalOpen();
  };
  const onNewItemSelect = () => {
    setSelectedItemId(null);
    setIsNew(true);
    onMasterModalOpen();
  };
  const onCloseMasterModal = () => {
    onMasterModalClose();
    getMasterEquipment(config.key);
    onReloadItems();
  };

  // itemsが変わったらlocalItemsを初期化（display_order順 or id順）
  useEffect(() => {
    if (!items) return;
    const sorted = [...items].sort((a, b) => {
      if (a.display_order !== undefined && b.display_order !== undefined) {
        return a.display_order - b.display_order;
      } else if (a.display_order !== undefined) {
        return -1;
      } else if (b.display_order !== undefined) {
        return 1;
      } else {
        return (a.id || 0) - (b.id || 0);
      }
    });
    setLocalItems(sorted);
    setOrderChanged(false);
  }, [items]);

  // 並び替えハンドラ
  const moveItem = (index, direction) => {
    const newItems = [...localItems];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newItems.length) return;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    setLocalItems(newItems);
    setOrderChanged(true);
  };

  // 並び順保存処理
  const handleSaveOrderInternal = async () => {
    setSavingOrder(true);
    try {
      const updatedItems = localItems.map((item, idx) => ({ ...item, display_order: idx }));

      for (const item of updatedItems) {
        await updateMasterEquipment(config.key, item.id, { display_order: item.display_order });
      }

      toast({
        title: "並び順を保存しました",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setOrderChanged(false);
      onReloadItems();
    } catch (error) {
      console.error('並び順の保存に失敗:', error);
      toast({
        title: "並び順の保存に失敗しました",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSavingOrder(false);
    }
  };





  // refを通じてhandleSaveOrderInternalを公開
  React.useImperativeHandle(ref, () => ({
    handleSaveOrderInternal
  }));

  return (
    <Box position="relative">
    {selectedItems.length > 0 && (
    <Box position="fixed" width="100%" bottom="20px" bg="gray.100" p={4} borderRadius="md" borderWidth="1px" borderColor="gray.300" zIndex={1000}>
      <VStack spacing={3}>
        <Heading size="sm">一括操作 ({selectedItems.length}件選択中)</Heading>
        
        {/* 画像アップロードセクション */}
        <Box borderWidth="1px" borderColor="gray.300" borderRadius="md" p={3} bg="white" w="100%">
          <Text fontSize="sm" fontWeight="bold" mb={2}>画像アップロード</Text>
          <VStack spacing={2}>
            <Input
              placeholder="画像の説明を入力（オプション）"
              value={bulkDescription}
              onChange={(e) => setBulkDescription(e.target.value)}
              size="sm"
            />
            <Checkbox
              size="sm"
              isChecked={useBulkFilenameAsDescription}
              onChange={(e) => setUseBulkFilenameAsDescription(e.target.checked)}
            >
              <Text fontSize="xs" color="gray.600">
                ファイル名を画像の注釈として使用する
              </Text>
            </Checkbox>
            <Input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                await handleUploadImageMulti(file, selectedItems, bulkDescription);
              }}
              disabled={uploadingMulti}
            />
            {uploadingMulti && <Text fontSize="xs" color="gray.500">アップロード中...</Text>}
          </VStack>
        </Box>
        
        {/* 設備タイプ変更セクション */}
        {equipmentDefinitions?.equipment_types && (
          <Box borderWidth="1px" borderColor="gray.300" borderRadius="md" p={3} bg="white" w="100%">
            <Text fontSize="sm" fontWeight="bold" mb={2}>設備タイプ変更</Text>
            <Menu>
              <MenuButton as={Button} size="sm" variant="outline" w="100%">
                設備タイプを選択
              </MenuButton>
              <MenuList>
                {Object.entries(equipmentDefinitions.equipment_types)
                  .filter(([key, definition]) => key !== config.key) // 現在のタイプは除外
                  .sort((a, b) => (a[1].display_order || 0) - (b[1].display_order || 0))
                  .map(([key, definition]) => (
                    <MenuItem
                      key={key}
                      onClick={() => handleBulkChangeType(key)}
                    >
                      {definition.icon} {definition.name}
                    </MenuItem>
                  ))}
              </MenuList>
            </Menu>
          </Box>
        )}
        
        {/* 削除セクション */}
        <Button
          size="sm"
          colorScheme="red"
          leftIcon={<DeleteIcon />}
          onClick={handleBulkDelete}
          variant="outline"
          w="100%"
        >
          選択した設備を削除 ({selectedItems.length}件)
        </Button>
      </VStack>
    </Box>
    )}
    <Grid
      templateColumns={{
        base: 'repeat(1, 1fr)',
        sm: 'repeat(2, 1fr)',
        md: 'repeat(2, 1fr)',
        lg: 'repeat(2, 1fr)'
      }}
      gap={4}
    >
        <GridItem>
        <Box
          borderWidth="1px"
          borderColor={tileBorder}
          borderRadius="md"
          p={4}
          bg={tileBg}
          boxShadow="sm"
          transition="background 0.2s, box-shadow 0.2s"
          cursor="pointer" // _hover={{ bg: useColorModeValue('blue.50', 'blue.900'), boxShadow: 'md' }}
          _hover={{ bg: useColorModeValue('gray.100', 'gray.700'), boxShadow: 'md' }}
          onClick={onNewItemSelect}
          display="flex"
          alignItems="center"
          justifyContent="center"
          h="100%"
        >
          <VStack spacing={2}>
            <Box
              w="70px"
              h="70px"
              borderRadius="md"
              bg={useColorModeValue('gray.200', 'gray.600')}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <AddIcon boxSize="24px" color={useColorModeValue('black', 'white')} />
            </Box>
            <Text color={useColorModeValue('black', 'white')} fontWeight="bold">
              マスタ{config.key==='floorplan' ? '間取り' : '設備'}を登録
            </Text>
          </VStack>
        </Box>
        </GridItem>

      {localItems.map((item, idx) => {
        return (
        <GridItem key={item.id}>
          <Box
            bg={cardBg}
            borderWidth="1px"
            borderColor={cardBorder}
            borderRadius="lg"
            overflow="hidden"
            position="relative"
            transition="transform 0.2s"
            _hover={{ transform: 'translateY(-2px)' }}
            display="flex"
            flexDirection="column"
            h="100%"
          >
              <Flex p={2} justify="space-between" align="center" bg={useColorModeValue('gray.50', 'gray.700')}>
                <Checkbox
                  size="lg"
                  colorScheme="gray"
                  isChecked={selectedItems.includes(item.id)}
                  onChange={(e) => handleMultiSelectChange(item.id, e)}
                />
                <HStack spacing={1}>
                  <IconButton
                    size="xs"
                    icon={<ArrowUpIcon />}
                    aria-label="上へ"
                    onClick={() => moveItem(idx, -1)}
                    isDisabled={idx === 0 || savingOrder}
                  />
                  <IconButton
                    size="xs"
                    icon={<ArrowDownIcon />}
                    aria-label="下へ"
                    onClick={() => moveItem(idx, 1)}
                    isDisabled={idx === localItems.length - 1 || savingOrder}
                  />
                </HStack>
              </Flex>

            <Box position="relative" onClick={() => onItemSelect(item.id)} cursor="pointer" flex="1">
              {getImageUrl(item) ? (
                <Image
                  src={getImageUrl(item)}
                  alt={item.name}
                  w="100%"
                  h="250px"
                  objectFit="contain"
                />
              ) : (
                <Flex w="100%" h="250px" bg="gray.100" align="center" justify="center" color="gray.500">
                  画像なし
                </Flex>
              )}
              <IconButton
                size="sm"
                icon={<DeleteIcon />}
                colorScheme="red"
                aria-label="削除"
                position="absolute"
                top={2}
                right={2}
                onClick={async (e) => {
                  e.stopPropagation(); // 親要素のonClickイベントを抑制
                  if (!window.confirm('本当にこの設備を削除してもよいですか？')) return;
                  try {
                    await deleteMasterEquipment(config.key, item.id);
                    toast({ title: "アイテムを削除しました", status: "success", duration: 3000, isClosable: true });
                    onReloadItems();
                  } catch (error) {
                    toast({ title: "エラー", description: error.message, status: "error" });
                  }
                }}
                isRound
              />
            </Box>


            <VStack p={4} align="stretch" spacing={2} onClick={() => onItemSelect(item.id)} cursor="pointer" flex="1">
              <Flex justify="space-between" align="center">
                <Text fontSize="sm" color={subTextColor}>
                  {item.manufacturer}
                </Text>
                 {item.recommended && (
                  <Badge colorScheme="black">おすすめ</Badge>
                )}
              </Flex>
              <Text
                fontSize="md"
                fontWeight="bold"
                color={textColor}
                noOfLines={2}
              >
                {item.name}
              </Text>
              <Box fontSize="lg" fontWeight="bold" color="black">
                {formatPrice(item.price)}
              </Box>
              <Text fontSize="sm" color={subTextColor} noOfLines={2}>
                {item.features}
              </Text>

              {config.fields?.map(field => {
                const value = item[field.name];
                if (value === undefined || value === null || 
                    field.name === 'price' || field.name === 'features' || 
                    field.name === 'recommended' || field.name === 'manufacturer' || 
                    field.name === 'name' || field.name === 'images' ||
                    (item.is_user_equipment && field.name === 'user_id') ||
                    field.showInCard === false) return null;
                // floorplanのm2のみ単位切替
                if (config.key === 'floorplan' && field.unit === 'm2') {
                  return (
                    <Text key={field.name} fontSize="sm" color={subTextColor}>
                      {field.label}: {isTsubo ? (value ? (Number(value) / 3.305785).toFixed(2) + '坪' : '') : value + 'm²'}
                    </Text>
                  );
                }
                return (
                  <Text key={field.name} fontSize="sm" color={subTextColor}>
                    {field.label}: {typeof value === 'boolean' ? (value ? 'あり' : 'なし') : value}{field.unit || ''}
                  </Text>
                );
              })}
            </VStack>
          </Box>
        </GridItem>
        );
      })}
    </Grid>
    <MasterSelectedEquipmentModal
      item={selectedItem}
      config={config}
      isTsubo={isTsubo}
      isOpen={isMasterModalOpen}
      onClose={onCloseMasterModal}
      isNew={isNew}
      onReloadItems={onReloadItems}
    />

    </Box>
  );
}); 

export default function MasterEquipment() {
  const { currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [equipmentDefinitions, setEquipmentDefinitions] = useState({});
  const [definitionsLoading, setDefinitionsLoading] = useState(true);

  // --- State ---
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState('');
  const [fixedMap, setFixedMap] = useState({});
  const isWide = useBreakpointValue({ base: false, md: true });
  const sidebarDisclosure = useDisclosure({ defaultIsOpen: isWide });
  const [isTsubo, setIsTsubo] = useState(false); // 単位切替（坪）
  const navigate = useNavigate();
  const toast = useToast();
  const fileInputRef = useRef();
  const [isUploading, setIsUploading] = useState(false);

  const [orderChanged, setOrderChanged] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const masterEquipmentListRef = useRef();

  // 設備定義を取得
  const loadEquipmentDefinitions = useCallback(async () => {
    if (!currentUser) {
      setDefinitionsLoading(false);
      setError("ユーザー情報がありません。");
      return;
    }
    setDefinitionsLoading(true);
    setError(null);
    try {
      const definitions = await getMasterEquipmentDefinitions();
      if (!definitions || Object.keys(definitions).length === 0) {
        throw new Error('設備定義が空です');
      }
      setEquipmentDefinitions(definitions);
      
      // 設備定義一覧の最初の要素を初期表示に設定
      if (definitions.equipment_types && Object.keys(definitions.equipment_types).length > 0) {
        const sortedEquipmentTypes = Object.entries(definitions.equipment_types)
          .sort((a, b) => (a[1].display_order || 0) - (b[1].display_order || 0));
        const firstEquipmentKey = sortedEquipmentTypes[0][0];
        setSelectedEquipment(firstEquipmentKey);
      }
      // 設備定義一覧が空の場合はselectedEquipmentを空文字のままにする
    } catch (err) {
      console.error("設備定義の取得に失敗しました:", err);
      setError(err.message);
      setEquipmentDefinitions({});
      // エラー時もselectedEquipmentを空文字のままにする
    } finally {
      setDefinitionsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadEquipmentDefinitions();
  }, [loadEquipmentDefinitions]);




  // 現在選択中の設備の設定を取得
  const currentEquipmentConfig = useMemo(() => {
    if (definitionsLoading || Object.keys(equipmentDefinitions).length === 0) {
      let label = '設備定義を読込中...';

      if (!definitionsLoading && Object.keys(equipmentDefinitions).length === 0) {
        label = error ? '設備定義読込エラー' : '設備定義なし';
      }
      return {
        key: selectedEquipment,
        label: label,
        enabled: false,
        enable_popularity: true,
        enable_guide: true,
        icon: '❓',
        fields: []
      };
    }

    const definition = equipmentDefinitions['equipment_types'][selectedEquipment];
    if (!definition) {
      console.warn(`設備定義が見つかりません: ${selectedEquipment}`);
      return {
        key: selectedEquipment,
        label: '定義が見つかりません',
        enabled: false,
        enable_popularity: true,
        enable_guide: true,
        icon: '❓',
        fields: []
      };
    }
    return {
      key: selectedEquipment,
      label: definition.name,
      enabled: definition.enabled,
      enable_popularity: definition.enable_popularity,
      enable_guide: definition.enable_guide,
      icon: definition.icon,
      fields: Object.entries(definition.fields || {}).map(([name, field]) => ({
        name,
        ...field
      }))
    };
  }, [selectedEquipment, equipmentDefinitions, definitionsLoading, error]);


// 設備アイテムとユーザー設定の読み込み (selectedEquipment や targetUserId が変わった時に実行)
const loadItems = useCallback(async () => {
  if (!currentUser || definitionsLoading || !equipmentDefinitions.equipment_types || !equipmentDefinitions.equipment_types[selectedEquipment]) {
    if (!definitionsLoading && equipmentDefinitions.equipment_types && !equipmentDefinitions.equipment_types[selectedEquipment]) {
      console.warn(`選択された設備 (${selectedEquipment}) の定義が equipmentDefinitions.equipment_types に見つかりません。`);
    }
    setLoading(false);
    return;
  }
  setLoading(true);
  setError(null);
  try {
    const itemsData = await getMasterEquipment(selectedEquipment);
    setItems(itemsData);
  } catch (err) {
    console.error("アイテム・設定データの取得に失敗しました:", err);
    setError(err.message);
    setItems([]);
  } finally {
    setLoading(false);
  }
}, [currentUser, selectedEquipment, equipmentDefinitions, definitionsLoading]);

// selectedEquipment, targetUserId, または definitionsLoading の完了後にアイテムと設定を読み込む
useEffect(() => {
  if (!definitionsLoading) { // definitionsLoading が false になってから実行
    loadItems();
  }
}, [loadItems, definitionsLoading]); // definitionsLoading も依存配列に追加


  // --- ここで isFloorplanSelected を定義 ---
  const isFloorplanSelected = useMemo(() => {
    return selectedEquipment === 'floorplan';
  }, [selectedEquipment]);

  // 選択されたアイテム
  const selectedItem = useMemo(() => {
    if (selectedItemId === null) return null;
    return items.find(item => item.id === selectedItemId);
  }, [selectedItemId, items]);

  // ハンドラー
  const handleItemSelect = (itemId) => { setSelectedItemId(itemId); };
  const closeModal = () => { setSelectedItemId(null); };

  // 画面幅が変わったら開閉を自動で切り替える
  useEffect(() => {
    if (isWide && !sidebarDisclosure.isOpen) {
      sidebarDisclosure.onOpen();
    } else if (!isWide && sidebarDisclosure.isOpen) {
      sidebarDisclosure.onClose();
    }
    // eslint-disable-next-line
  }, [isWide]);

  // Excelエクスポート
  const handleExportExcel = () => {
    if (!items || items.length === 0) {
      toast({ title: 'データがありません', status: 'warning' });
      return;
    }
    const unUseFields = ['images', 'rooms', 'created_at', 'updated_at',
        '_rid', '_self', '_etag', '_attachments', '_ts', 'type', 'tenant_id', 'display_order'
    ];
    
    // fieldsで定義されたすべてのフィールド名を取得（unUseFieldsを除く）
    const definedFields = ['id']; // idは常に先頭に配置
    if (currentEquipmentConfig && currentEquipmentConfig.fields) {
      currentEquipmentConfig.fields.forEach(field => {
        if (!unUseFields.includes(field.name) && field.name !== 'id') {
          definedFields.push(field.name);
        }
      });
    }
    
    const data = items.map(item => {
      // 定義されたフィールドすべてを含むオブジェクトを作成
      const obj = {};
      definedFields.forEach(fieldName => {
        obj[fieldName] = item[fieldName] || ''; // 存在しないフィールドは空文字
      });
      
      // 定義されていないが実際に存在するフィールドも追加（unUseFieldsを除く）
      Object.keys(item).forEach(key => {
        if (!unUseFields.includes(key) && !definedFields.includes(key)) {
          obj[key] = item[key];
        }
      });
      
      return obj;
    });
    
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'MasterEquipment');
    XLSX.writeFile(wb, `設備マスタ_${selectedEquipment}.xlsx`);
  };

  // Excelインポート
  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const result = await bulkUpdateMasterEquipment(selectedEquipment, file);
      
      // デフォルト数量が適用されたアイテムがある場合の通知
      if (result.applied_default_quantities && result.applied_default_quantities.length > 0) {
        const appliedItems = result.applied_default_quantities;
        const totalApplied = appliedItems.reduce((sum, item) => sum + item.updated_count + item.created_count, 0);
        const itemCount = appliedItems.length;
        
        toast({
          title: `アップロード完了しました`,
          description: `デフォルト数量が設定された${itemCount}件の設備を、テナント内の全ユーザ（${totalApplied}件）に適用しました。`,
          status: "success",
          duration: 10000,
          isClosable: true,
        });
      } else {
        toast({
          title: `アップロード完了しました`,
          duration: 8000,
          isClosable: true,
        });
      }
    } catch (err) {
        toast({
            title: `アップロード失敗しました。エラー確認し、再度Excelのダウンロード、編集、アップロードをお願いします。`,
            description: err.message,
            status: 'error',
            duration: 30000,
            isClosable: true,
          });
    } finally {
        loadItems();
        setIsUploading(false);
    }
  };

  // 並び順保存
  const handleSaveOrder = () => {
    // MasterEquipmentListのhandleSaveOrderInternalを呼び出す
    if (masterEquipmentListRef.current) {
      masterEquipmentListRef.current.handleSaveOrderInternal();
    }
  };

  if (loading || definitionsLoading) {
    return (
      <Flex align="center" justify="center" minH="60vh">
        <Text fontSize="xl" color="gray.500">データを読み込み中...</Text>
      </Flex>
    );
  }

  // --- Chakra UI用の色 ---
  const mainBg = useColorModeValue('white', 'gray.900');

  return (
    <Flex minH="100vh" w="100vw" bg={mainBg}>
      {/* サイドバー */}
      <Box
        w={sidebarDisclosure.isOpen ? (isWide ? '220px' : '100vw') : '60px'}
        minW={sidebarDisclosure.isOpen && !isWide ? '100vw' : '60px'}
        bg="gray.50"
        borderRightWidth={isWide ? "1px" : "0px"}
        p={2}
        position={!isWide && sidebarDisclosure.isOpen ? "fixed" : "relative"}
        top={!isWide && sidebarDisclosure.isOpen ? "120px" : "auto"}
        left={!isWide && sidebarDisclosure.isOpen ? "0" : "auto"}
        zIndex={!isWide && sidebarDisclosure.isOpen ? "1000" : "auto"}
        h={!isWide && sidebarDisclosure.isOpen ? "calc(100vh - 120px)" : "auto"}
        overflowY={!isWide && sidebarDisclosure.isOpen ? "auto" : "visible"}
      > 
        <IconButton
          icon={sidebarDisclosure.isOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          onClick={sidebarDisclosure.onToggle}
          aria-label="ナビを開閉"
          mb={2}
          size="sm"
          colorScheme="gray"
          isRound
        />
        <EquipmentSelector
          onSelect={setSelectedEquipment}
          selected={selectedEquipment}
          fixedMap={fixedMap}
          equipmentList={equipmentDefinitions}
          isOpen={sidebarDisclosure.isOpen}
          showEstimateButton={false}
        />
      </Box>
      {/* メインコンテンツ */}
      <Box flex={1} px={{ base: 1, md: 4 }} minW={0}>
        {/* 管理者用の追加ボタン */}
        <Flex align="center" justify="flex-end" px={4} py={2} gap={2}>
          <Button bg="black" color="white" onClick={() => navigate('/admin/master-equipment/excluded-items')}>見積もり外項目管理</Button>
          <Button bg="black" color="white" onClick={() => navigate('/admin/master-equipment/master-equipment-definitions')}>設備定義管理</Button>
        </Flex>
        <Flex gap={4} position="relative" flexDirection="column">
          {/* 単位切替スイッチ（floorplanのみ） */}
          {selectedEquipment === 'floorplan' && (
            <Flex align="center" mb={4} gap={4} px={4} bg="gray.50" p={3} borderRadius="md" borderWidth="1px" borderColor="gray.200">
              <Text fontSize="sm" fontWeight="medium">面積単位：</Text>
              <Text fontSize="sm">m²</Text>
              <Switch isChecked={isTsubo} onChange={e => setIsTsubo(e.target.checked)} colorScheme="teal" />
              <Text fontSize="sm">坪</Text>
            </Flex>
          )}
          
          {/* 左側のメインコンテンツ */}
          <Box flex={1} bg="gray.100" minW="0" transition="width 0.3s ease-in-out" w="100%" border="1px solid gray" p={4}>
          {/* Excelエクスポート・インポートUI */}
          <Flex gap={2} mt={2} flexDirection="column">
            <Button width="150px" size="sm" bg="black" color="white" onClick={handleExportExcel}>Excel出力</Button>
            <Heading fontSize="sm">Excelアップロード</Heading>
            <Input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              onChange={handleImportExcel}
              isDisabled={isUploading}
            />
            {isUploading && <Spinner size="sm" />}
            <Heading fontSize="sm">Excelアップロードの際はこちらご注意ください</Heading>
            <Text fontSize="xs" color="black">
              ※新規作成は行を追加して入力ください。id列には、"new" を入力してください。
            </Text>
            <Text fontSize="xs" color="black">
              ※設備の削除はできません。画面上で削除ください。
            </Text>
            <Text fontSize="xs" color="black">
              ※更新したい設備のid列は編集しないでください。
            </Text>
          </Flex>
          </Box>
          <Box 
            flex={1} 
            minW="0" 
            transition="width 0.3s ease-in-out"
            w="100%"
          >
            {error && (
              <Alert status="error" mb={4} borderRadius="md">
                <AlertIcon />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <Flex direction="column" gap={4} w="100%">
              <Box w="100%">
                <MasterEquipmentList
                  ref={masterEquipmentListRef}
                  items={items}
                  config={currentEquipmentConfig}
                  equipmentDefinitions={equipmentDefinitions}
                  isTsubo={isTsubo}
                  onReloadItems={loadItems}
                  orderChanged={orderChanged}
                  setOrderChanged={setOrderChanged}
                  savingOrder={savingOrder}
                  setSavingOrder={setSavingOrder}
                />
              </Box>
            </Flex>
          </Box>
        </Flex>

        {selectedItem && (
            <Box>
                <Text>選択されたアイテム</Text>
            </Box>
        )}
      </Box>
      
      {/* 並び順保存ボタン - 画面下部に固定（並び順が変更された時のみ表示） */}
      {orderChanged && (
        <Box
          position="fixed"
          bottom={4}
          right={4}
          zIndex={1000}
        >
          <Button 
            colorScheme="yellow" 
            onClick={handleSaveOrder} 
            isLoading={savingOrder}
            size="lg"
            boxShadow="lg"
          >
            並び順を保存
          </Button>
        </Box>
      )}
    </Flex>
  );
} 